/* eslint-disable react-refresh/only-export-components */
import { Alert } from 'src/modules/shared/components/atoms/alert';
import { ComponentVariant } from 'src/modules/shared/types/component.type';
import TimedAlertModal from 'components/molecules/modals/timed-alert-modal';
import React, { createContext, useContext, useState, ReactNode } from 'react';

export type AlertType = 'success' | 'error' | 'warning';

interface AlertContextType {
  showAlert: (message: string, type: AlertType) => void;
  showModalAlert: (
    message: string,
    title?: string,
    icon?: ReactNode,
    durationInMs?: number
  ) => void;
}

const AlertContext = createContext<AlertContextType>({
  showAlert: () => {},
  showModalAlert: () => {},
});

export const useAlert = () => useContext(AlertContext);

interface AlertProviderProps {
  children: ReactNode;
}

const ALERT_TIMEOUT_MS: number = 5000;

const alertTypeToVariant: Record<AlertType, ComponentVariant> = {
  success: 'success',
  error: 'danger',
  warning: 'warning',
};

export const AlertProvider: React.FC<AlertProviderProps> = ({ children }) => {
  const [alert, setAlert] = useState<{
    message: string;
    variant: ComponentVariant;
  } | null>(null);

  const [modalAlert, setModalAlert] = useState<{
    message: string;
    durationInMs: number;
    title?: string;
    icon?: ReactNode;
  } | null>(null);

  const showAlert = (message: string, type: AlertType) => {
    setAlert({ message, variant: alertTypeToVariant[type] });
    setTimeout(() => setAlert(null), ALERT_TIMEOUT_MS);
  };

  const showModalAlert = (
    message: string,
    title?: string,
    icon?: ReactNode,
    durationInMs = ALERT_TIMEOUT_MS
  ) => {
    setModalAlert({ message, title, icon, durationInMs });
    setTimeout(() => setModalAlert(null), durationInMs);
  };

  return (
    <AlertContext.Provider value={{ showAlert, showModalAlert }}>
      {children}
      {alert && (
        <div
          className="fixed z-[10000] top-6 left-0 right-0 w-screen flex  
        items-start justify-center p-4 cursor-pointer"
          onClick={() => setAlert(null)}
          role="button"
          tabIndex={0}
        >
          <Alert
            message={alert.message}
            variant={alert.variant}
            appearance="soft"
            size="md"
            className="w-full md:max-w-6xl shadow-md"
          />
        </div>
      )}
      {modalAlert && (
        <TimedAlertModal
          message={modalAlert.message}
          durationInMs={modalAlert.durationInMs}
          title={modalAlert.title}
          icon={modalAlert.icon}
          isOpen={!!modalAlert}
          onClose={() => setModalAlert(null)}
        />
      )}
    </AlertContext.Provider>
  );
};
